import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { esMenorDeEdad, edadAniosMeses } from '@practiclinic/core'
import { AppShell } from './comunes/AppShell.js'
import { Nav } from './comunes/Nav.js'
import { BannerPaciente } from './comunes/BannerPaciente.js'
import type { Navegar, SolapaPaciente, Vista } from './comunes/navegacion.js'
import { Login } from './comunes/Login.js'
import { Timeline } from './recepcion/Timeline.js'
import { NuevaCita } from './recepcion/NuevaCita.js'
import { DetalleCita } from './recepcion/DetalleCita.js'
import { Hoy } from './recepcion/Hoy.js'
import { Dinero } from './recepcion/Dinero.js'
import { Pacientes } from './recepcion/Pacientes.js'
import { Ficha } from './paciente/Ficha.js'
import { Anamnesis } from './paciente/Anamnesis.js'
import { Odontograma } from './paciente/Odontograma.js'
import { Cuenta } from './paciente/Cuenta.js'
import { Citas } from './paciente/Citas.js'
import { Reportes } from './recepcion/Reportes.js'
import { Configuracion } from './admin/Configuracion.js'
import { Consultorios } from './operativa/Consultorios.js'
import { MiDia } from './profesional/MiDia.js'
import { Atencion } from './atencion/Atencion.js'
import { arrancarSync, prepararParaTenant } from './datos/sync.js'
import { logout, quienSoy, type Sesion } from './datos/sesion.js'
import { conflictosAbiertos, diaDeCita, solicitudesPendientes } from './datos/consultas.js'
import { db } from './datos/db.js'
import { hoy } from './datos/formato.js'

/**
 * El arranque de la PWA operativa.
 *
 * Dos perfiles de UI, no un solo responsive (§7.1): recepción trabaja en una
 * tablet horizontal con la grilla completa y el teclado; el profesional usa el
 * celular a una mano y sólo ve su día. Meter los dos en la misma pantalla con
 * media queries termina en una interfaz que no sirve para ninguno.
 *
 * El layout lo pone `AppShell` y nada más: acá sólo se decide **qué** se
 * muestra, nunca dónde ni con qué ancho.
 */

/**
 * Las pantallas que administran su propio ancho y su propio scroll. Hoy es una
 * sola: la grilla de la agenda. El paciente dejó de estar acá porque su lista y
 * sus solapas viven en el ancho de texto del shell, como el resto.
 */
const PLENAS: Vista[] = ['agenda']

type PanelAgenda =
  | { tipo: 'nueva'; recursoId: string; inicio: string }
  | { tipo: 'detalle'; citaId: string }
  | null

export function App() {
  const [sesion, setSesion] = useState<Sesion | null>(null)
  const [cargando, setCargando] = useState(true)
  const [vista, setVista] = useState<Vista>('hoy')
  const [panelAgenda, setPanelAgenda] = useState<PanelAgenda>(null)
  const [panelAtencion, setPanelAtencion] = useState<string | null>(null)
  const [dia, setDia] = useState(hoy())
  const [citaEnfocada, setCitaEnfocada] = useState<string | null>(null)
  const [agendarPara, setAgendarPara] = useState<string | null>(null)
  /** El eje del paciente: quién está abierto, y con qué solapa. */
  const [paciente, setPaciente] = useState<string | null>(null)
  const [solapa, setSolapa] = useState<SolapaPaciente>('ficha')

  // Al abrir la app se pregunta si la cookie sigue viva. Si no hay red la
  // pregunta falla, y ahí es donde el offline tiene que aguantar: con sesión
  // previa en memoria se sigue trabajando contra Dexie.
  useEffect(() => {
    quienSoy()
      .then(setSesion)
      .catch(() => setSesion(null))
      .finally(() => setCargando(false))
  }, [])

  // El sincronizador arranca sólo cuando hay sesión: sin ella, cada pull
  // volvería 401 y el backoff se llenaría de ruido.
  //
  // Antes se comprueba de qué tenant son los datos guardados. Si son de otro
  // —cambió de clínica, o la base del servidor se recreó— hay que vaciarlos:
  // el sync incremental no puede enterarse de borrados que ocurrieron sin
  // tombstone, y lo viejo quedaría conviviendo con lo nuevo para siempre.
  useEffect(() => {
    if (!sesion) return
    let detener: (() => void) | undefined
    void prepararParaTenant(sesion.tenant.id).then(() => {
      detener = arrancarSync()
    })
    return () => detener?.()
  }, [sesion])

  const navegar: Navegar = (d) => {
    setPanelAgenda(null)
    setPanelAtencion(null)
    setCitaEnfocada(d.cita ?? null)
    setAgendarPara(d.agendarPara ?? null)
    // El paciente es contexto, no destino: un destino que no lo nombra **no lo
    // cierra**. Eso es todo lo que hace falta para que mirar la agenda o los
    // cobros no obligue a buscarlo de nuevo por nombre. Lo cierra sólo el × del
    // banner.
    if (d.cliente) {
      setPaciente(d.cliente)
      setSolapa(d.solapa ?? 'ficha')
    } else if (d.solapa) {
      setSolapa(d.solapa)
    }
    setVista(d.vista)
    if (d.cita) {
      void diaDeCita(d.cita).then((suyo) => {
        const destino = suyo ?? d.dia
        if (destino) setDia(destino)
      })
    } else if (d.dia) {
      setDia(d.dia)
    }
  }

  // El contador de la bandeja: vive acá porque la bandeja dejó de ser pestaña y
  // pasó a ser un aviso sobre el módulo que la contiene.
  const pendientes = useLiveQuery(
    async () => (await solicitudesPendientes()).length + (await conflictosAbiertos()).length,
    [],
    0,
  )

  // Lo que muestra el banner del paciente abierto. El saldo va arriba y no
  // dentro de la ficha porque es lo que decide la conversación en el mostrador:
  // tiene que estar a la vista en todas las solapas.
  const abierto = useLiveQuery(
    async () => {
      if (!paciente) return null
      const cliente = await db.cliente.get(paciente)
      if (!cliente) return null
      const citas = await db.cita.where('cliente_id').equals(paciente).toArray()
      const ids = citas.map((c) => c.id)
      const cargos = await db.cargo.filter((g) => ids.includes(g.cita_id)).toArray()
      const pagos = await db.pago.toArray()
      const deuda = cargos.reduce((total, g) => {
        const pagado = pagos
          .filter((x) => x.cargo_id === g.id)
          .reduce((a, x) => a + x.monto, 0)
        return total + Math.max(0, g.monto - pagado)
      }, 0)
      let edad: string | null = null
      let esMenor = false
      if (cliente.fecha_nacimiento) {
        esMenor = esMenorDeEdad(cliente.fecha_nacimiento, new Date())
        const obj = edadAniosMeses(cliente.fecha_nacimiento, new Date())
        if (obj) {
          edad = `${obj.anios} años${obj.anios < 6 ? ` ${obj.meses} meses` : ''}`
        }
      }
      return { cliente, deuda, edad, esMenor }
    },
    [paciente],
    null,
  )

  if (cargando) {
    return (
      <main className="flex min-h-dvh items-center justify-center text-slate-500">
        Abriendo la agenda…
      </main>
    )
  }

  if (!sesion) return <Login onEntrar={setSesion} />

  // El profesional no usa el shell de escritorio: su vista es una lista para el
  // celular, con su propia barra (§7.1).
  if (sesion.rol === 'profesional') {
    if (panelAtencion) {
      return (
        <Atencion
          citaId={panelAtencion}
          onCerrar={() => setPanelAtencion(null)}
          destinoPago="profesional"
        />
      )
    }
    return sesion.recursoPersonaId ? (
      <MiDia
        recursoPersonaId={sesion.recursoPersonaId}
        onAtencion={(citaId) => setPanelAtencion(citaId)}
      />
    ) : (
      // El CHECK de la base lo impide, pero si alguna vez llega acá es mejor
      // decirlo que mostrar una agenda vacía sin explicación.
      <main className="flex min-h-dvh items-center justify-center p-8 text-center text-slate-500">
        Tu usuario no está vinculado a una agenda. Pedile a la administración que lo asocie.
      </main>
    )
  }

  return (
    <AppShell
      pleno={PLENAS.includes(vista)}
      {...(abierto
        ? {
            banner: (
              <BannerPaciente
                nombre={abierto.cliente.nombre}
                telefono={abierto.cliente.telefono}
                deuda={abierto.deuda}
                edad={abierto.edad}
                esMenor={abierto.esMenor}
                solapa={solapa}
                // Navegar y no sólo cambiar de solapa: el banner se ve desde
                // cualquier módulo, así que tocar «Odontograma» estando en Hoy
                // tiene que llevar al paciente. Antes marcaba la solapa y el
                // contenido no cambiaba, que es la peor clase de botón: el que
                // responde al clic y no hace nada.
                onSolapa={(s) => navegar({ vista: 'pacientes', cliente: abierto.cliente.id, solapa: s })}
                onCerrar={() => setPaciente(null)}
              />
            ),
          }
        : {})}
      nav={
        <Nav
          sesion={sesion}
          vista={vista}
          onVista={(v) => navegar({ vista: v })}
          pendientes={pendientes}
          onSalir={() => {
            void logout().then(() => setSesion(null))
          }}
        />
      }
    >
      {vista === 'config' && sesion.rol === 'admin' ? (
        <Configuracion slotMin={15} />
      ) : vista === 'consultorios' ? (
        <Consultorios />
      ) : vista === 'dinero' ? (
        <Dinero
          dia={dia}
          onDia={setDia}
          citaEnfocada={citaEnfocada}
          onVerPaciente={(cliente) => navegar({ vista: 'pacientes', cliente })}
        />
      ) : vista === 'reportes' ? (
        <Reportes
          onCobrar={(cita, diaReporte) => navegar({ vista: 'dinero', cita, dia: diaReporte })}
        />
      ) : vista === 'pacientes' ? (
        // El eje del paciente: con uno abierto se muestra su solapa; sin
        // ninguno, la lista para elegirlo. Volver a la lista es cerrarlo.
        paciente === null ? (
          <Pacientes
            elegido={paciente}
            onElegir={(cliente) => navegar({ vista: 'pacientes', cliente })}
          />
        ) : solapa === 'odontograma' ? (
          <Odontograma clienteId={paciente} citaId={null} />
        ) : solapa === 'anamnesis' ? (
          <Anamnesis clienteId={paciente} esMenor={abierto?.esMenor ?? false} />
        ) : solapa === 'cuenta' ? (
          <Cuenta
            clienteId={paciente}
            onCobrar={(cita) => navegar({ vista: 'dinero', cita })}
          />
        ) : solapa === 'citas' ? (
          <Citas
            clienteId={paciente}
            onAbrirCita={(cita) => navegar({ vista: 'agenda', cita })}
            onAgendar={(cliente) => navegar({ vista: 'agenda', agendarPara: cliente })}
          />
        ) : (
          <Ficha
            clienteId={paciente}
            onAnamnesis={() => navegar({ vista: 'pacientes', cliente: paciente, solapa: 'anamnesis' })}
            onCobrar={(cita) => navegar({ vista: 'dinero', cita })}
            onAgendar={(cliente) => navegar({ vista: 'agenda', agendarPara: cliente })}
          />
        )
      ) : vista === 'hoy' ? (
        <Hoy
          dia={dia}
          onDia={setDia}
          onAtender={(cita) => {
            setPanelAgenda(null)
            setPanelAtencion(cita)
          }}
          onCobrar={(cita) => navegar({ vista: 'dinero', cita })}
          onVerPaciente={(cliente) => navegar({ vista: 'pacientes', cliente })}
          onAbrirCita={(cita) => navegar({ vista: 'agenda', cita })}
        />
      ) : (
        <>
          <Timeline
            dia={dia}
            onDia={setDia}
            slotMin={15}
            citaEnfocada={citaEnfocada}
            agendarPara={agendarPara}
            onCancelarAgendar={() => setAgendarPara(null)}
            onNuevaCita={(recursoId, inicio) =>
              setPanelAgenda({ tipo: 'nueva', recursoId, inicio })
            }
            onAbrirCita={(citaId) => setPanelAgenda({ tipo: 'detalle', citaId })}
          />
          {panelAgenda?.tipo === 'nueva' && (
            <NuevaCita
              recursoId={panelAgenda.recursoId}
              inicioSugerido={panelAgenda.inicio}
              clientePrefijado={agendarPara}
              onCerrar={() => {
                setPanelAgenda(null)
                setAgendarPara(null)
              }}
            />
          )}
          {panelAgenda?.tipo === 'detalle' && (
            <DetalleCita
              citaId={panelAgenda.citaId}
              onCerrar={() => setPanelAgenda(null)}
              onVerFicha={(cliente) => navegar({ vista: 'pacientes', cliente })}
              // Entrar a atender reemplaza el detalle, no se monta encima: si
              // no, los dos paneles quedan superpuestos y la atención aparece
              // cortada detrás del lateral.
              onAtencion={(citaId) => {
                setPanelAgenda(null)
                setPanelAtencion(citaId)
              }}
            />
          )}
        </>
      )}

      {/* La atención se monta acá y no dentro de la agenda: se entra a atender
          desde la grilla, desde el detalle de la cita y desde «Hoy», y las tres
          tienen que abrir la misma pantalla. Es un overlay a pantalla completa,
          así que no le importa qué vista quedó abajo. */}
      {panelAtencion && (
        <Atencion
          citaId={panelAtencion}
          onCerrar={() => setPanelAtencion(null)}
          onCobrar={(cita) => navegar({ vista: 'dinero', cita })}
          destinoPago="caja"
        />
      )}
    </AppShell>
  )
}
